import { appStore } from "../state/store";
import { ProgressBar } from "./ProgressBar";
export class LifePriorities {
    constructor(app) {
        this.app = app;
        // Chart interaction state
        this.svg = null;
        this.size = 400;
        this.center = this.size / 2;
        this.activePointIndex = null;
        this.progressBar = new ProgressBar(6, 1);
        // The component's internal state is now hydrated from the single source of truth.
        // It maps the store's data structure to its own internal working format.
        this.priorities = appStore.getState().user.onboardingInputs.lifePriorities.map(p => ({
            id: p.id,
            name: p.name,
            weight: p.weight
        }));
    }
    // --- Chart Logic (No changes needed here) ---
    getPointCoordinates(index, value) {
        const angle = (Math.PI * 2 * index) / this.priorities.length - Math.PI / 2;
        const radius = (value / 100) * (this.center * 0.8);
        return {
            x: this.center + radius * Math.cos(angle),
            y: this.center + radius * Math.sin(angle),
        };
    }
    updateChart() {
        var _a;
        if (!this.svg)
            return;
        const points = this.priorities.map((p, i) => this.getPointCoordinates(i, p.weight));
        const areaPath = points.map(p => `${p.x},${p.y}`).join(' ');
        (_a = this.svg.querySelector('.radar-area')) === null || _a === void 0 ? void 0 : _a.setAttribute('points', areaPath);
        points.forEach((p, i) => {
            var _a;
            const pointEl = (_a = this.svg) === null || _a === void 0 ? void 0 : _a.querySelector(`#radar-point-${i}`);
            pointEl === null || pointEl === void 0 ? void 0 : pointEl.setAttribute('cx', String(p.x));
            pointEl === null || pointEl === void 0 ? void 0 : pointEl.setAttribute('cy', String(p.y));
        });
        this.updatePriorityList();
    }
    updatePriorityList() {
        const listEl = document.getElementById('priority-list-display');
        if (!listEl)
            return;
        const sortedPriorities = [...this.priorities].sort((a, b) => b.weight - a.weight);
        const listHtml = sortedPriorities.map((p, index) => `
            <li class="priority-item">
                <span class="rank">Rank #${index + 1}</span>
                <span class="name">${p.name}</span>
                <span class="weight">${p.weight}</span>
            </li>
        `).join('');
        listEl.innerHTML = listHtml;
    }
    // --- Interaction Handlers (No changes needed here) ---
    onDragStart(index) {
        this.activePointIndex = index;
    }
    onDrag(event) {
        if (this.activePointIndex === null || !this.svg)
            return;
        event.preventDefault();
        const rect = this.svg.getBoundingClientRect();
        const clientX = 'touches' in event ? event.touches[0].clientX : event.clientX;
        const clientY = 'touches' in event ? event.touches[0].clientY : event.clientY;
        const svgX = (clientX - rect.left) * (this.size / rect.width);
        const svgY = (clientY - rect.top) * (this.size / rect.height);
        const dx = svgX - this.center;
        const dy = svgY - this.center;
        const angle = (Math.PI * 2 * this.activePointIndex) / this.priorities.length - Math.PI / 2;
        const projectedRadius = dx * Math.cos(angle) + dy * Math.sin(angle);
        let newWeight = Math.round((projectedRadius / (this.center * 0.8)) * 100);
        newWeight = Math.max(0, Math.min(100, newWeight));
        this.priorities[this.activePointIndex].weight = newWeight;
        this.updateChart();
    }
    onDragEnd() {
        this.activePointIndex = null;
    }
    // --- Component Lifecycle (Render logic is now dynamic based on constructor) ---
    render() {
        const levels = 5;
        const gridHtml = Array.from({ length: levels }).map((_, i) => {
            const radius = (this.center * 0.8 * (i + 1)) / levels;
            const points = Array.from({ length: this.priorities.length }).map((_, j) => {
                const angle = (Math.PI * 2 * j) / this.priorities.length - Math.PI / 2;
                return `${this.center + radius * Math.cos(angle)},${this.center + radius * Math.sin(angle)}`;
            }).join(' ');
            return `<polygon class="radar-grid" points="${points}" />`;
        }).join('');
        const axesHtml = this.priorities.map((_, i) => {
            const endPoint = this.getPointCoordinates(i, 100);
            return `<line class="radar-axis" x1="${this.center}" y1="${this.center}" x2="${endPoint.x}" y2="${endPoint.y}" />`;
        }).join('');
        const labelsHtml = this.priorities.map((p, i) => {
            const pos = this.getPointCoordinates(i, 115);
            return `<text class="radar-label" x="${pos.x}" y="${pos.y}">${p.name}</text>`;
        }).join('');
        const points = this.priorities.map((p, i) => this.getPointCoordinates(i, p.weight));
        const areaPath = points.map(p => `${p.x},${p.y}`).join(' ');
        const pointsHtml = points.map((p, i) => `<circle id="radar-point-${i}" class="radar-point" cx="${p.x}" cy="${p.y}" r="8" />`).join('');
        return `
            <div class="card">
                ${this.progressBar.render()}
                <h2>Step 1: Your Core Life Priorities</h2>
                <p>What is your "Why"? Drag the points to set the weight for each motivation. This creates the highest level of your personal <strong>Hierarchy</strong> of values.</p>
                
                <div class="radar-chart-container">
                    <svg class="radar-chart-svg" viewBox="0 0 ${this.size} ${this.size}">
                        ${gridHtml}
                        ${axesHtml}
                        <polygon class="radar-area" points="${areaPath}" />
                        ${pointsHtml}
                        ${labelsHtml}
                    </svg>
                </div>

                <div id="priority-list-display"></div>

                <div class="button-container">
                    <button id="next-btn" class="button-primary">Next</button>
                </div>
            </div>
        `;
    }
    // --- Writing data back to store remains correct ---
    attachEventListeners() {
        var _a;
        this.svg = document.querySelector('.radar-chart-svg');
        if (!this.svg)
            return;
        this.svg.addEventListener('mousemove', this.onDrag.bind(this));
        this.svg.addEventListener('mouseup', this.onDragEnd.bind(this));
        this.svg.addEventListener('mouseleave', this.onDragEnd.bind(this));
        this.svg.addEventListener('touchmove', this.onDrag.bind(this), { passive: false });
        this.svg.addEventListener('touchend', this.onDragEnd.bind(this));
        this.priorities.forEach((_, i) => {
            var _a;
            const pointEl = (_a = this.svg) === null || _a === void 0 ? void 0 : _a.querySelector(`#radar-point-${i}`);
            pointEl === null || pointEl === void 0 ? void 0 : pointEl.addEventListener('mousedown', () => this.onDragStart(i));
            pointEl === null || pointEl === void 0 ? void 0 : pointEl.addEventListener('touchstart', () => this.onDragStart(i), { passive: false });
        });
        (_a = document.getElementById('next-btn')) === null || _a === void 0 ? void 0 : _a.addEventListener('click', () => {
            const sortedPriorities = [...this.priorities].sort((a, b) => b.weight - a.weight);
            const finalPriorities = this.priorities.map(p => (Object.assign(Object.assign({}, p), { rank: sortedPriorities.findIndex(sp => sp.id === p.id) + 1 })));
            appStore.updateLifePriorities(finalPriorities);
            this.app.nextStep();
        });
        this.updatePriorityList();
    }
}
